import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAuthCSRFToken, SessionExpiredError } from './auth';

type CSRFRetryRequestConfig = InternalAxiosRequestConfig & {
  csrfRetry?: boolean;
};

export type InternalAPIError = {
  success: boolean;
  error: any;
  status: number;
};

/**
 * Intercept all requests sent via this instance
 * and add in the auth token and default headers.
 *
 * @param {boolean} requireToken Whether or not a token is required to make this request.
 *
 * @return {AxiosInstance} Returns Axios client.
 */
export default (requireToken: boolean = true) => {
  const client = axios.create({
    baseURL: `/`
  });
  if (requireToken) {
    client.interceptors.request.use(
      async (config) => {
        const csrfToken = await getAuthCSRFToken();
        config.headers.set('Content-Type', 'application/json');
        config.headers.set('X-Csrf-Token', csrfToken);
        return config;
      },
      (error) => {
        if (error instanceof SessionExpiredError) {
          console.debug('Request aborted — session expired');
          return Promise.reject(error);
        }
        console.debug('CSRF token retrieval failed', error);
        return Promise.reject(new Error('Token not retrievable'));
      }
    );
    client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const requestConfig = error.config as CSRFRetryRequestConfig | undefined;

        if (error.response?.status !== 401 || !requestConfig || requestConfig.csrfRetry) {
          return Promise.reject(error);
        }

        requestConfig.csrfRetry = true;

        try {
          const csrfToken = await getAuthCSRFToken({ forceRefresh: true });
          requestConfig.headers.set('X-Csrf-Token', csrfToken);
          return client(requestConfig);
        } catch (tokenError) {
          if (tokenError instanceof SessionExpiredError) {
            console.debug('Request retry aborted — session expired');
          } else {
            console.debug('CSRF token refresh failed before retry', tokenError);
          }

          return Promise.reject(error);
        }
      }
    );
  }
  return client;
};

/**
 * Handles errors when the internalAPI interceptor is used.
 *
 * @param {any} error
 * @return {InternalAPIError}
 */
export const handleInternalAPIError = (error: any) => {
  // request was made but did not pass validateStatus (200-300)
  if (error.response) {
    return { success: false, status: error.response.status, data: {} };
  }
  // Internal API failed to refresh token
  if (error.status && error.status == 401) {
    return { success: false, status: error.status, data: {} };
  }
  // unhandled error
  return { success: false, status: 0, data: {} };
};
